import { Store } from '../../store/store';

export type GuidedTrainingRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export type GuidedTrainingCoachPosition = {
  top: number;
  left: number;
  width: number;
};

type TargetRectOptions = {
  padding?: number | ((targetId: string) => number);
  multiple?: boolean;
};

type CoachPositionOptions = {
  maxWidth: number;
  height: number;
  defaultMaxWidth?: number;
  defaultLeft?: 'center' | 'right';
  defaultTopMin?: number;
  defaultTopRatio?: number;
  preferRight?: boolean;
};

type DomSyncOptions = {
  syncPosition: () => void;
  activeBodyClass?: string;
  onKeyDown?: (evt: KeyboardEvent) => void;
  retryAttempts?: number;
  retryDelayMs?: number;
};

type GuidedTrainingLibrary = {
  hideUserDetails?: boolean | null;
};

export const hasVisibleGuidedTrainingLibrary = (
  libraries: readonly GuidedTrainingLibrary[],
) => libraries.some(library => !library.hideUserDetails);

export const guidedTrainingTargetSelector = (id: string) =>
  `[data-guided-training-id="${id}"]`;

export const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export const getGuidedTrainingStorageKey = (
  store: Store,
  prefix: string,
  suffix?: string,
) => {
  const userKey = store.user?.email || store.user?.name || 'anonymous';
  return [prefix, suffix, userKey].filter(Boolean).join('.');
};

export const getGuidedTrainingTargetRect = (
  targetId?: string,
  options: TargetRectOptions = {},
): GuidedTrainingRect | null => {
  if (!targetId) return null;

  const selector = guidedTrainingTargetSelector(targetId);
  const nodes = options.multiple
    ? Array.from(document.querySelectorAll<HTMLElement>(selector))
    : [document.querySelector<HTMLElement>(selector)].filter((node): node is HTMLElement => !!node);

  if (nodes.length === 0) return null;

  const rects = nodes.map(node => node.getBoundingClientRect());
  const top = Math.min(...rects.map(rect => rect.top));
  const left = Math.min(...rects.map(rect => rect.left));
  const right = Math.max(...rects.map(rect => rect.right));
  const bottom = Math.max(...rects.map(rect => rect.bottom));
  const padding = typeof options.padding === 'function'
    ? options.padding(targetId)
    : options.padding ?? 8;

  return {
    top: Math.max(8, top - padding),
    left: Math.max(8, left - padding),
    width: Math.min(window.innerWidth - 16, right - left + padding * 2),
    height: Math.min(window.innerHeight - 16, bottom - top + padding * 2),
  };
};

export const getGuidedTrainingCoachPosition = (
  rect: GuidedTrainingRect | null,
  options: CoachPositionOptions,
): GuidedTrainingCoachPosition => {
  const maxWidth = rect ? options.maxWidth : options.defaultMaxWidth ?? options.maxWidth;
  const width = Math.min(maxWidth, window.innerWidth - 32);
  const height = options.height;

  if (!rect) {
    const top = clamp(
      window.innerHeight * (options.defaultTopRatio ?? 0.25),
      options.defaultTopMin ?? 120,
      window.innerHeight - height - 24,
    );
    const left = options.defaultLeft === 'right'
      ? clamp(window.innerWidth - width - 56, 16, window.innerWidth - width - 16)
      : (window.innerWidth - width) / 2;

    return { width, top, left };
  }

  const preferRight = options.preferRight ?? true;
  const rightLeft = rect.left + rect.width + 24;
  const canFitRight = preferRight && rightLeft + width + 16 < window.innerWidth;
  const left = canFitRight
    ? rightLeft
    : clamp(rect.left + rect.width / 2 - width / 2, 16, window.innerWidth - width - 16);
  const belowTop = rect.top + rect.height + 18;
  const aboveTop = rect.top - height - 18;
  const top = canFitRight
    ? clamp(rect.top, 24, window.innerHeight - height - 24)
    : belowTop + height < window.innerHeight
      ? belowTop
      : Math.max(24, aboveTop);

  return { width, top, left };
};

export class GuidedTrainingDomSync {
  constructor(private readonly options: DomSyncOptions) {}

  private frameId: number | null = null;
  private retryTimeoutId: number | null = null;
  private remainingPositionRetries = 0;

  get canRetry(): boolean {
    return this.remainingPositionRetries > 0;
  }

  schedulePositionSync(retryMissingTarget = false) {
    this.cancelPositionSync();
    this.remainingPositionRetries = retryMissingTarget ? this.options.retryAttempts ?? 0 : 0;

    this.frameId = window.requestAnimationFrame(() => {
      this.frameId = null;
      this.options.syncPosition();
    });
  }

  schedulePositionRetry() {
    this.cancelPositionSync(false);
    this.remainingPositionRetries -= 1;

    this.retryTimeoutId = window.setTimeout(() => {
      this.retryTimeoutId = null;
      this.frameId = window.requestAnimationFrame(() => {
        this.frameId = null;
        this.options.syncPosition();
      });
    }, this.options.retryDelayMs ?? 50);
  }

  cancelPositionSync(resetRetries = true) {
    if (this.frameId !== null) {
      window.cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }

    if (this.retryTimeoutId !== null) {
      window.clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    if (resetRetries)
      this.remainingPositionRetries = 0;
  }

  bindListeners() {
    this.unbindListeners();

    if (this.options.activeBodyClass)
      document.body.classList.add(this.options.activeBodyClass);

    window.addEventListener('resize', this.options.syncPosition);
    window.addEventListener('scroll', this.options.syncPosition, true);

    if (this.options.onKeyDown)
      window.addEventListener('keydown', this.options.onKeyDown);
  }

  unbindListeners() {
    if (this.options.activeBodyClass)
      document.body.classList.remove(this.options.activeBodyClass);

    window.removeEventListener('resize', this.options.syncPosition);
    window.removeEventListener('scroll', this.options.syncPosition, true);

    if (this.options.onKeyDown)
      window.removeEventListener('keydown', this.options.onKeyDown);
  }
}
